import type { ReactNode } from 'react';
import { TabsTrigger } from '@/components/ui/tabs';

type ScreenTabProps = { value: string; icon: ReactNode; label: string };

/**
 * One tab of a details screen's `?screen=` strip: the experiment page's, and the feature flag
 * page's, which follows it.
 *
 * The tab strip is a console control, not a page heading: mono micro-label. The type
 * classes are spelled out rather than using `micro-label`, so tailwind-merge can drop
 * the `text-sm` the shared TabsTrigger sets - which it can only do because the scale
 * steps are registered as font sizes in `helpers/cn`.
 *
 * The active state is deliberately not set here. It used to be `bg-field` over the
 * list's `bg-panel`, and those are two percent apart in both themes, so the current
 * tab was almost impossible to pick out. It now comes from the `[role='tab']` rule in
 * globals.css, which uses the selection tokens the design specifies for an active tab.
 */
export const ScreenTab = ({ value, icon, label }: ScreenTabProps) => (
  <TabsTrigger
    value={value}
    className="gap-1.5 rounded-lg px-3 py-1.5 font-mono text-nano font-medium tracking-[0.1em] uppercase shadow-none transition-colors duration-150 data-[state=active]:shadow-none"
  >
    {icon}
    {label}
  </TabsTrigger>
);

/** The strip the tabs sit in. */
export const SCREEN_TABS_LIST_CLASS = 'border-line bg-panel h-auto w-fit gap-1 rounded-lg border p-1';
